import React from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Avatar,
    Stack,
    List,
    ListItem,
    ListItemIcon,
    ListItemText, Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FastForwardIcon from '@mui/icons-material/FastForward';

export default function Welcome() {

    const scientificAxes = [
        {
            title: "Ingénierie des Flux & IoT",
            subtitle: "Capture et continuité Edge-to-Cloud",
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
                    <path d="M12 10V6" /><path d="M12 3v1" />
                </svg>
            ),
            color: "#1a237e", // Deep Blue
            points: [
                "Systèmes de capture pour données massives et hétérogènes",
                "Garantie de l'intégrité et de la provenance à la source",
                "Optimisation des flux en conditions de réseau variables"
            ],
            opening: "Fiabiliser la genèse de la donnée scientifique"
        },
        {
            title: "Interopérabilité Sémantique",
            subtitle: "Modélisation et Abstraction",
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <line x1="3" y1="12" x2="9" y2="12" />
                    <line x1="15" y1="12" x2="21" y2="12" />
                    <line x1="12" y1="3" x2="12" y2="9" />
                    <line x1="12" y1="15" x2="12" y2="21" />
                </svg>
            ),
            color: "#6a1b9a", // Purple
            points: [
                "Découplage des couches stockage et traitement",
                "Ontologies et modèles pour l'unification de silos de données",
                "Standards de métadonnées pour la réutilisabilité (FAIR)"
            ],
            opening: "Briser les silos par l'abstraction logicielle"
        },
        {
            title: "Infrastructures Scalables",
            subtitle: "Cloud, HPC et Big Data",
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                    <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                    <line x1="6" y1="6" x2="6" y2="6" />
                    <line x1="6" y1="18" x2="6" y2="18" />
                </svg>
            ),
            color: "#00796b", // Teal
            points: [
                "Déploiement de 'Data Factories' à l'échelle du pétaoctet",
                "Architecture micro-services et Service Meshing",
                "Optimisation des performances en environnements distribués"
            ],
            opening: "Industrialiser le traitement des données massives"
        },
        {
            title: "Services pour l'e-Science",
            subtitle: "Valorisation et aide à la décision",
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="7.5 4.21 12 6.81 16.5 4.21" />
                    <polyline points="7.5 19.79 7.5 14.63 3 12" />
                    <polyline points="21 12 16.5 14.63 16.5 19.79" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
            ),
            color: "#d32f2f", // Red
            points: [
                "Interfaces de visualisation pour données complexes",
                "Outils de support pour les communautés scientifiques",
                "Passage à l'échelle des algorithmes d'analyse (IA)"
            ],
            opening: "Transformer la donnée brute en connaissance"
        }
    ];

    return (
        <Box sx={{ py: 6, px: 2, maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ mb: 6, textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: '#1a237e', letterSpacing: -0.5 }}>
                    Benoit Lange
                </Typography>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2, fontWeight: 400 }}>
                    Expertise en Sciences des Données & Ingénierie logicielle
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    <Chip label="Cloud / HPC" size="small" variant="outlined" />
                    <Chip label="Interopérabilité" size="small" variant="outlined" />
                    <Chip label="Data Engineering" size="small" variant="outlined" />
                    <Chip label="IoT & Flux" size="small" variant="outlined" />
                    <Chip label="e-Science" size="small" variant="outlined" />
                    <Chip label="Santé" size="small" variant="outlined" />
                    <Chip label="Environement" size="small" variant="outlined" />
                </Stack>
            </Box>


            <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800, mb: 1 }}>
                POSITIONNEMENT SCIENTIFIQUE & RECHERCHE
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6, fontWeight: 400 }}>
                Cycle de vie de la donnée : de la capture à la valorisation
            </Typography>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                },
                gap: 3,
                alignItems: 'stretch',
            }}>
                {scientificAxes.map((axis, index) => (
                    <Card key={index} sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 4,
                    }}>
                            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                    <Avatar sx={{ bgcolor: `${axis.color}15`, color: axis.color, width: 48, height: 48 }}>
                                        <Box sx={{ width: 28, height: 28 }}>{axis.icon}</Box>
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                            {axis.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {axis.subtitle}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <List sx={{ p: 0 }}>
                                    {axis.points.map((pt, i) => (
                                        <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                                                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: axis.color }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={pt}
                                                primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem' } }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>

                            <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <FastForwardIcon sx={{ fontSize: 14, color: axis.color }} />
                                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Objectif
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                    {axis.opening}
                                </Typography>
                            </Box>
                    </Card>
                ))}
            </Box>
            <Box sx={{ mt: 6, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                    Cycle de vie de la donnee scientifique
                </Typography>
                <Box
                    component="img"
                    src="/images/DIKS.jpg"
                    alt="Schema du cycle de vie de la donnee : gerer, organiser, analyser et valoriser"
                    sx={{
                        width: '100%',
                        maxWidth: 1200,
                        borderRadius: 3,
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
                    }}
                />
            </Box>

        </Box>
    );
}